import { useLingui } from '@lingui/react/macro';
import { Chip, SxProps, Theme } from '@mui/material';

import useLocale from '@watershed/intl/frontend/useLocale';
import DateTimeUtils from '@watershed/shared-universal/utils/DateTimeUtils';
import mixinSx from '@watershed/style/mixinSx';

import Avatar from './Avatar';
import Tooltip from './Tooltip';
import TruncatingTypographyWithTooltip from './TruncatingTypographyWithTooltip';

interface DatePillProps {
  date: Date;
  dateEventType?: DateEventType;
  userName?: string;
  isWatershedEmployee?: boolean;
  showTooltip?: boolean;
  sx?: SxProps<Theme>;
}

export type DateEventType = 'updated' | 'created' | 'submitted';

export default function DatePill({
  date,
  userName,
  sx,
  isWatershedEmployee,
  showTooltip,
  dateEventType,
}: DatePillProps) {
  const { t } = useLingui();
  const locale = useLocale();

  const formattedDate = DateTimeUtils.formatDate(
    DateTimeUtils.fromJSDate(date),
    { format: 'med', locale }
  );
  const formattedDateTime = DateTimeUtils.formatDateTime(
    DateTimeUtils.fromJSDate(date),
    { format: 'med', locale }
  );

  const chip = (
    <Chip
      component="time"
      dateTime={date.toISOString()}
      variant="outlined"
      // Use icon and not avatar prop otherwise styles get overwritten.
      icon={
        userName ? (
          <Avatar variant="person" name={userName} size="small" />
        ) : undefined
      }
      label={
        // This allows the label to truncate and show a ... with longer strings
        <TruncatingTypographyWithTooltip
          variant="body1"
          sx={{
            color: (theme) => theme.palette.grey50,
            fontWeight: 'bold',
            userSelect: 'none',
          }}
          tooltip={formattedDate}
        />
      }
      tabIndex={showTooltip ? 0 : undefined}
      sx={mixinSx(
        {
          borderColor: (theme) => theme.palette.grey30,
          fontSize: (theme) => theme.typography.body2.fontSize,
          borderRadius: 2,
          display: 'flex',
          gap: 0.25,
          px: 0.5,
          width: 'fit-content',
          '& .MuiChip-label': {
            py: 0,
            px: 0.25,
          },
          '& .MuiChip-icon': {
            display: 'flex',
            alignItems: 'center',
            m: 0,
            p: 0,
            color: (theme) => theme.palette.white,
          },
        },
        !showTooltip && {
          '&.MuiChip-root:hover': {
            backgroundColor: (theme) => theme.palette.background.paper,
          },
        },
        sx
      )}
    />
  );

  if (!showTooltip) return chip;

  const userNameWithWsEmployeeSuffix = isWatershedEmployee
    ? t({
        message: `${userName} (Watershed employee)`,
        context: 'Date pill tooltip',
      })
    : userName;

  let titleForTooltip = null;
  switch (dateEventType) {
    case 'created':
      titleForTooltip = userNameWithWsEmployeeSuffix
        ? t({
            message: `Created on ${formattedDateTime} by ${userNameWithWsEmployeeSuffix}`,
            context: 'Date pill tooltip',
          })
        : t({
            message: `Created on ${formattedDateTime}`,
            context: 'Date pill tooltip',
          });
      break;
    case 'submitted':
      titleForTooltip = userNameWithWsEmployeeSuffix
        ? t({
            message: `Submitted on ${formattedDateTime} by ${userNameWithWsEmployeeSuffix}`,
            context: 'Date pill tooltip',
          })
        : t({
            message: `Submitted on ${formattedDateTime}`,
            context: 'Date pill tooltip',
          });
      break;
    case 'updated':
    default:
      titleForTooltip = userNameWithWsEmployeeSuffix
        ? t({
            message: `Last updated on ${formattedDateTime} by ${userNameWithWsEmployeeSuffix}`,
            context: 'Date pill tooltip',
          })
        : t({
            message: `Last updated on ${formattedDateTime}`,
            context: 'Date pill tooltip',
          });
      break;
  }

  return <Tooltip title={titleForTooltip}>{chip}</Tooltip>;
}
